import {
  isDateStringValid,
  isMinimumAge,
  isPasswordValidLength,
  isPhoneNumberValid,
} from 'tally-validation';
import {number, ref, string} from 'yup';

import {BirthSex, MeasurementUnit} from '#apollo/schema';
import {Pronoun} from '#constant/prounouns';

export const birthSexValidator = string()
  .nullable()
  .oneOf(
    [BirthSex.Female, BirthSex.Male, BirthSex.Unknown],
    'Please select one of the birth sex options',
  )
  .required('This field is required');

export const numberValidator = number()
  .typeError('Please enter a valid number')
  .nullable()
  .min(0, 'Please enter a positive number')
  .test(
    'maxFloatSize',
    'Please enter a maximum of two decimal places',
    value => !value || /^\d*(\.\d{0,2})?$/.test(`${value}`),
  )
  .transform((_, val) => (val !== '' ? Number(val) : null));

export const stringValidator = string().trim();

export const feetValidator = stringValidator
  .test(
    'matchFormat',
    'Please enter height in the given format',
    value => !value || /^.'.."/.test(`${value}`),
  )
  .test(
    'matchFormat',
    'A foot contains twelve inches',
    value => !value || /^[\d]'((0[0-9])|(1[0-1]))"/.test(`${value}`),
  );

export const emailValidator = stringValidator
  .email('Please enter a valid email')
  .required('Please enter a valid email');

export const passwordValidator = stringValidator
  .test('min', 'Password must be at least 8 digits long', isPasswordValidLength)
  .required('Please enter a password');

export const confirmPasswordValidator = (fieldName = 'password') =>
  stringValidator
    .nullable()
    .required('Please re-enter your password')
    .oneOf([ref(fieldName)], 'The password you have entered does not match the confirmation');

export const dateValidator = stringValidator
  .transform(value => {
    // At this point the order of the date parts is Month, Date, and Year.
    // We want the order to be Year, Month, and Date for isDateStringValid.
    const dateParts = value.split('/', 3);
    return [dateParts[2], dateParts[0], dateParts[1]].join('-');
  })
  .test('date', 'Please enter a valid date', value => {
    return value ? isDateStringValid(value) : false;
  });

export const minimumAgeValidator = dateValidator.test(
  'minimum-age',
  'We’re sorry, you must be 18 years or older to participate',
  value => (value ? isMinimumAge(value) : false),
);

export const measurementUnitValidator = stringValidator.oneOf(
  [MeasurementUnit.Imperial, MeasurementUnit.Metric],
  'Please select a valid unit type',
);

export const measurementUnitRequiredValidator = measurementUnitValidator.required(
  'Please select a valid unit type',
);

export const pronounValidator = stringValidator.oneOf(
  Object.values(Pronoun),
  'Please select one of the provided options',
);

export const heightValidator = (fieldName = 'heightDisplayUnit') =>
  stringValidator.when(fieldName, fieldValue => {
    for (const value of fieldValue) {
      return value === MeasurementUnit.Metric ? numberValidator : feetValidator;
    }
    return feetValidator;
  });

export const phoneValidator = stringValidator
  .transform(value => `+1${value.split('-', 3).join('')}`)
  .test('phoneNumber', 'Please enter a valid phone number', value => {
    return value ? isPhoneNumberValid(value) : false;
  });

export const barcodeValidatorRegex = /^(TH)?(\d{6})/;
export const barcodeValidator = stringValidator.test(
  'matchFormat',
  'Vial number is invalid',
  // Match barcodes that are made up of exactly 6 digits and optionally prefixed by `TH`.
  // Matches: TH123456 and 123456
  val => !!val?.match(barcodeValidatorRegex),
);
